import { useEffect, useMemo, useState } from 'react';
import { Box, Label } from '@nimbus-ds/components';
import { Status } from '@tiendanube/common/src/enums';
import { Button, Input, Select, Stack } from '@tiendanube/components';
import ModalAside from 'App/components/lab/ModalAside';
import { FEATURE_SEARCH_FILTER } from 'App/features';
import { DateFilter } from 'commons/components';
import MultiSelect from 'commons/components/MultiSelect';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import { ORDERS_SHOW_FILTERS } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import {
  OrderApplicationsFilter,
  OrderPaymentMethodsFilter,
} from 'domains/Orders/components';
import OrderPaymentProvidersFilter from 'domains/Orders/components/OrderPaymentProvidersFilter';
import { ProductInterface } from 'domains/Orders/components/ProductSearcher';
import ProductsFilter from 'domains/Orders/components/ProductsFilter';
import { ProductsFilterValue } from 'domains/Orders/components/ProductsFilter/ProductsFilter';
import useGetOrdersFilters from 'domains/Orders/Orders/hooks/useGetOrdersFilters/useGetOrdersFilters';
import useIsProductsFilterEnabled from 'domains/Orders/Orders/hooks/useIsProductsFilterEnabled';
import {
  ExportFiltersType,
  FilterStatusEnum,
} from 'domains/Orders/Orders/ordersService';
import { validateDateLimit } from 'domains/Orders/Orders/utils/OrderListFilter';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useOrdersExportFilters } from '../../hooks';

interface OrdersExportFiltersModalProps {
  show: boolean;
  filters: ExportFiltersType;
  onConfirm: (filters: ExportFiltersType) => void;
  onClose: () => void;
  selectedProducts: ProductInterface<true>[];
  serializeProducts: (products: ProductInterface<true>[]) => string;
}

function OrdersExportFiltersModal({
  show,
  filters,
  onConfirm,
  onClose,
  selectedProducts,
  serializeProducts,
}: OrdersExportFiltersModalProps) {
  const t = useTranslationOrders();
  const {
    values,
    statusOptions,
    fulfillmentStatusOptions,
    paymentOptions,
    paymentMethodOptions,
    paymentProviderOptions,
    shippingMethodOptions,
    originOptions,
    handleChange,
    handleDateRangeChange,
    handleMultiSelectChange,
    setAllValues,
    applicationOptions,
  } = useOrdersExportFilters(filters);
  const { fetchOrdersFilters } = useGetOrdersFilters();
  const hasProductsFilter = useIsProductsFilterEnabled();

  const [selectedProductsValues, setSelectedProductsValues] =
    useState(selectedProducts);

  const handleConfirm = () => {
    onConfirm(validateDateLimit(values));
    onClose();
  };

  const handleCancel = () => {
    setAllValues(filters);
    onClose();
  };

  const handleProductsFilterChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    callback: (value: ProductsFilterValue) => {
      setSelectedProductsValues(value.products);
      const serializedProducts = serializeProducts(value.products);
      const newValues = {
        products: serializedProducts,
        exactProductSet: value.exactProductSet,
      };
      setAllValues({ ...values, ...newValues });
    },
  });

  useEffect(() => {
    setAllValues(filters);
  }, [filters, setAllValues]);

  useEffect(() => {
    fetchOrdersFilters();
  }, [fetchOrdersFilters]);

  useEffect(() => {
    setSelectedProductsValues(selectedProducts);
  }, [selectedProducts]);

  const defaultFulfillmentStatus = useMemo(() => {
    if (values.fulfillmentStatus === FilterStatusEnum.EMPTY) return undefined;
    return values.fulfillmentStatus.split(',').map((option) => ({
      label: t(`filters.fulfillment.${option}`),
      value: option,
    }));
  }, [values.fulfillmentStatus, t]);

  const defaultPaymentStatus = useMemo(() => {
    if (values.paymentStatus === FilterStatusEnum.EMPTY) return undefined;
    return values.paymentStatus.split(',').map((option) => ({
      label: t(`filters.payment.${option}`),
      value: option,
    }));
  }, [values.paymentStatus, t]);

  return (
    <ModalAside
      isOpen={show}
      onDidDismiss={onClose}
      headerContent={t('exportPage.filtersTitle')}
      footer={
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          width="100%"
          gap="4"
          flex="1"
        >
          <Button onClick={handleCancel}>{`${t('filter.cancel')}`}</Button>
          <Button onClick={handleConfirm} appearance="primary">
            {`${t('filter.filter')}`}
          </Button>
        </Box>
      }
      footerHeight="70px"
    >
      <Stack column align="stretch">
        <Stack.Item>
          <Input
            name="q"
            label={t('filter.search')}
            placeholder={t('filter.searchPlaceholder')}
            value={values.q}
            onChange={handleChange}
          />
        </Stack.Item>
        <Stack.Item>
          <Select
            label={t('filter.status')}
            name="status"
            value={
              values.status === FilterStatusEnum.EMPTY
                ? Status.OPEN
                : values.status
            }
            options={statusOptions}
            onChange={handleChange}
          />
        </Stack.Item>
        <Stack.Item>
          <DateFilter
            label={t('filter.dateRange')}
            dateFrom={values.dateFrom}
            dateTo={values.dateTo}
            onChange={handleDateRangeChange}
            threeMonthsLimited={
              values.paymentMethods !== '' || values.paymentProvider !== ''
            }
          />
        </Stack.Item>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.paymentStatus')}</Label>
          <MultiSelect
            defaultValue={defaultPaymentStatus}
            options={paymentOptions as OptionType[]}
            placeholder={t('filters.payment.all')}
            onChange={(selected) =>
              handleMultiSelectChange('paymentStatus', selected)
            }
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.fulfillmentStatus')}</Label>
          <MultiSelect
            defaultValue={defaultFulfillmentStatus}
            options={fulfillmentStatusOptions as OptionType[]}
            placeholder={t('filters.fulfillment.all')}
            onChange={(selected) =>
              handleMultiSelectChange('fulfillmentStatus', selected)
            }
          />
        </Box>
        {hasProductsFilter && (
          <ProductsFilter
            selectedProducts={selectedProductsValues}
            exactProductSet={values.exactProductSet?.toLowerCase() === 'true'}
            onProductsFilterChange={handleProductsFilterChange}
          />
        )}
        <OrderPaymentMethodsFilter
          paymentMethods={values.paymentMethods}
          paymentMethodOptions={paymentMethodOptions}
          handleChange={handleChange}
        />
        <OrderPaymentProvidersFilter
          paymentProviders={values.paymentProvider}
          paymentProviderOptions={paymentProviderOptions}
          handleChange={handleChange}
        />
        <Stack.Item>
          <Select
            label={t('filter.shippingMethod')}
            name="shippingMethod"
            value={values.shippingMethod}
            options={shippingMethodOptions}
            onChange={handleChange}
          />
        </Stack.Item>
        <Stack.Item>
          <Select
            label={t('filter.origin')}
            name="origin"
            value={values.origin}
            options={originOptions}
            onChange={handleChange}
          />
        </Stack.Item>
        <OrderApplicationsFilter
          application={values.appId}
          applicationOptions={applicationOptions}
          handleChange={handleChange}
        />
      </Stack>
    </ModalAside>
  );
}

export default OrdersExportFiltersModal;
